import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { sessionData } from "../helpers/session-data";
import { casesService } from "../services/cases.service";
import type { CaseItem, CasesPagination, SearchCasesPayload } from "../services/cases.service";
import {
  getCasesFetchErrorMessage,
  parseCasesListResponse,
  parseSearchCasesResponse,
  parseClientCasesResponse,
} from "../utils/cases/casesFetchHelpers";

export interface CasesState {
  casesList: CaseItem[];
  pagination: CasesPagination;
  isLoading: boolean;
  isRefreshing: boolean;
  isLoadingMore: boolean;
  error: string | null;
  clientIdFilter: number | string | null;
  searchArgs: Omit<SearchCasesPayload, "business_id" | "device_token" | "session_id"> | null;
  activeListRequestId: string | null;
  listDataEpoch: number;
  initialAssignedFilterApplied: boolean;
}

const initialState: CasesState = {
  casesList: [],
  pagination: { total_cases: 0 },
  isLoading: false,
  isRefreshing: false,
  isLoadingMore: false,
  error: null,
  clientIdFilter: null,
  searchArgs: null,
  activeListRequestId: null,
  listDataEpoch: 0,
  initialAssignedFilterApplied: false,
};

export const fetchCases = createAsyncThunk(
  "cases/fetchCases",
  async (
    args: { page?: number; pageSize?: number; isRefresh?: boolean } | void,
    { rejectWithValue }
  ) => {
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id) {
      return rejectWithValue("Session not available");
    }
    const page = args && typeof args === "object" ? (args.page ?? 0) : 0;
    const pageSize = args && typeof args === "object" ? (args.pageSize ?? 10) : 10;
    const res = await casesService.getCases({
      business_id,
      device_token,
      session_id,
      page_number: page + 1,
      page_size: pageSize,
    });
    const parsed = parseCasesListResponse(res);
    if (parsed) {
      return {
        cases: parsed.cases as CaseItem[],
        pagination: parsed.pagination as unknown as CasesPagination,
      };
    }
    if (res.success === false) {
      return rejectWithValue(getCasesFetchErrorMessage(res));
    }
    return rejectWithValue(getCasesFetchErrorMessage(res, "Please try again."));
  }
);

export const appendNextCasesPage = createAsyncThunk(
  "cases/appendNextCasesPage",
  async (args: { pageSize: number; listDataEpoch: number }, { getState, rejectWithValue }) => {
    const state = getState() as { cases: CasesState };
    const c = state.cases;
    if (c.clientIdFilter !== null || c.searchArgs !== null) {
      return rejectWithValue("Cannot append in this list mode");
    }
    if (args.listDataEpoch !== c.listDataEpoch) {
      return rejectWithValue("Stale request");
    }
    if (c.pagination?.has_next !== true) {
      return rejectWithValue("No more pages");
    }
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id) {
      return rejectWithValue("Session not available");
    }
    const currentApiPage = c.pagination?.current_page ?? 1;
    const nextPageNumber = currentApiPage + 1;
    const res = await casesService.getCases({
      business_id,
      device_token,
      session_id,
      page_number: nextPageNumber,
      page_size: args.pageSize,
    });
    const parsed = parseCasesListResponse(res);
    if (parsed) {
      return {
        cases: parsed.cases as CaseItem[],
        pagination: parsed.pagination as unknown as CasesPagination,
        listDataEpoch: args.listDataEpoch,
      };
    }
    if (res.success === false) {
      return rejectWithValue(getCasesFetchErrorMessage(res));
    }
    return rejectWithValue(getCasesFetchErrorMessage(res, "Please try again."));
  },
  {
    condition: (_, { getState }) => {
      const s = (getState() as { cases: CasesState }).cases;
      return (
        !s.isLoading &&
        !s.isRefreshing &&
        !s.isLoadingMore &&
        s.pagination?.has_next === true &&
        s.clientIdFilter === null &&
        s.searchArgs === null
      );
    },
  }
);

export const fetchSearchCases = createAsyncThunk(
  "cases/fetchSearchCases",
  async (
    args: {
      search: Omit<SearchCasesPayload, "business_id" | "device_token" | "session_id">;
      page?: number;
      pageSize?: number;
      isRefresh?: boolean;
    },
    { rejectWithValue }
  ) => {
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id) {
      return rejectWithValue("Session not available");
    }
    const page = args.page ?? 0;
    const pageSize = args.pageSize ?? 10;
    const res = await casesService.searchCases({
      ...args.search,
      business_id,
      device_token,
      session_id,
      page_number: page + 1,
      page_size: pageSize,
    });
    const parsed = parseSearchCasesResponse(res);
    if (parsed) {
      return {
        cases: parsed.cases as CaseItem[],
        pagination: parsed.pagination as unknown as CasesPagination,
        search: args.search,
      };
    }
    if (res.success === false) {
      return rejectWithValue(getCasesFetchErrorMessage(res, "Failed to search cases"));
    }
    return rejectWithValue(getCasesFetchErrorMessage(res, "Please try again."));
  }
);

export const fetchClientCases = createAsyncThunk(
  "cases/fetchClientCases",
  async (
    args: {
      client_id: number | string;
      page?: number;
      pageSize?: number;
      isRefresh?: boolean;
    },
    { rejectWithValue }
  ) => {
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id) {
      return rejectWithValue("Session not available");
    }
    const page = args.page ?? 0;
    const pageSize = args.pageSize ?? 10;
    const res = await casesService.getClientCases({
      business_id,
      device_token,
      session_id,
      client_id: args.client_id,
      page_number: page + 1,
      page_size: pageSize,
    });
    const parsed = parseClientCasesResponse(res);
    if (parsed) {
      return {
        cases: parsed.cases as CaseItem[],
        pagination: parsed.pagination as unknown as CasesPagination,
        client_id: args.client_id,
      };
    }
    if (res.success === false) {
      return rejectWithValue(getCasesFetchErrorMessage(res, "Failed to fetch client cases"));
    }
    return rejectWithValue(getCasesFetchErrorMessage(res, "Please try again."));
  }
);

const casesSlice = createSlice({
  name: "cases",
  initialState,
  reducers: {
    setCasesList: (state, action: PayloadAction<CaseItem[]>) => {
      state.casesList = action.payload;
      state.error = null;
    },
    setCasesPagination: (state, action: PayloadAction<CasesPagination>) => {
      state.pagination = action.payload;
    },
    clearCasesError: (state) => {
      state.error = null;
    },
    clearCases: () => initialState,
    clearCasesClientScope: (state) => {
      state.clientIdFilter = null;
    },
    setClientIdFilter: (state, action: PayloadAction<number | string | null>) => {
      state.clientIdFilter = action.payload;
    },
    clearCasesListScope: (state) => {
      state.clientIdFilter = null;
      state.searchArgs = null;
      state.activeListRequestId = null;
      state.listDataEpoch += 1;
    },
    setCasesSearchArgs: (
      state,
      action: PayloadAction<Omit<
        SearchCasesPayload,
        "business_id" | "device_token" | "session_id"
      > | null>
    ) => {
      state.searchArgs = action.payload;
    },
    setInitialAssignedFilterApplied: (state, action: PayloadAction<boolean>) => {
      state.initialAssignedFilterApplied = action.payload;
    },
    removeCaseById: (state, action: PayloadAction<string | number>) => {
      const id = String(action.payload);
      state.casesList = (state.casesList ?? []).filter((c) => String(c.case_id ?? "") !== id);
      if (state.pagination?.total_cases && state.pagination.total_cases > 0) {
        state.pagination.total_cases = Math.max(0, state.pagination.total_cases - 1);
      }
    },
    patchCaseInList: (
      state,
      action: PayloadAction<{ caseId: string | number; patch: Partial<CaseItem> }>
    ) => {
      const id = String(action.payload.caseId);
      const list = state.casesList ?? [];
      const index = list.findIndex((c) => String(c.case_id ?? "") === id);
      if (index < 0) return;
      list[index] = { ...list[index], ...action.payload.patch };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCases.pending, (state, action) => {
        state.listDataEpoch += 1;
        state.activeListRequestId = action.meta.requestId;
        const arg = action.meta.arg;
        const isRefresh =
          arg && typeof arg === "object" && (arg as { isRefresh?: boolean }).isRefresh === true;
        if (isRefresh) {
          state.isRefreshing = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchCases.fulfilled, (state, action) => {
        if (state.activeListRequestId !== action.meta.requestId) return;
        state.casesList = action.payload.cases;
        state.pagination = action.payload.pagination;
        state.isLoading = false;
        state.isRefreshing = false;
        state.error = null;
      })
      .addCase(fetchCases.rejected, (state, action) => {
        if (state.activeListRequestId !== action.meta.requestId) return;
        state.isLoading = false;
        state.isRefreshing = false;
        state.error = (action.payload as string) ?? action.error.message ?? "Failed to load cases";
      })
      .addCase(fetchSearchCases.pending, (state, action) => {
        state.listDataEpoch += 1;
        state.activeListRequestId = action.meta.requestId;
        const isRefresh =
          action.meta.arg && (action.meta.arg as { isRefresh?: boolean }).isRefresh === true;
        if (isRefresh) state.isRefreshing = true;
        else state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSearchCases.fulfilled, (state, action) => {
        if (state.activeListRequestId !== action.meta.requestId) return;
        state.casesList = action.payload.cases;
        state.pagination = action.payload.pagination as CasesPagination;
        state.searchArgs = action.payload.search;
        state.clientIdFilter = null;
        state.isLoading = false;
        state.isRefreshing = false;
        state.error = null;
      })
      .addCase(fetchSearchCases.rejected, (state, action) => {
        if (state.activeListRequestId !== action.meta.requestId) return;
        state.isLoading = false;
        state.isRefreshing = false;
        state.error =
          (action.payload as string) ?? action.error.message ?? "Failed to search cases";
      })
      .addCase(fetchClientCases.pending, (state, action) => {
        state.listDataEpoch += 1;
        state.activeListRequestId = action.meta.requestId;
        const isRefresh =
          action.meta.arg && (action.meta.arg as { isRefresh?: boolean }).isRefresh === true;
        if (isRefresh) state.isRefreshing = true;
        else state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClientCases.fulfilled, (state, action) => {
        if (state.activeListRequestId !== action.meta.requestId) return;
        state.casesList = action.payload.cases;
        state.pagination = action.payload.pagination as CasesPagination;
        state.clientIdFilter = action.payload.client_id;
        state.searchArgs = null;
        state.isLoading = false;
        state.isRefreshing = false;
        state.error = null;
      })
      .addCase(fetchClientCases.rejected, (state, action) => {
        if (state.activeListRequestId !== action.meta.requestId) return;
        state.isLoading = false;
        state.isRefreshing = false;
        state.error =
          (action.payload as string) ?? action.error.message ?? "Failed to load client cases";
      })
      .addCase(appendNextCasesPage.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(appendNextCasesPage.fulfilled, (state, action) => {
        if (action.payload.listDataEpoch !== state.listDataEpoch) {
          state.isLoadingMore = false;
          return;
        }
        const seen = new Set(state.casesList.map((x) => String(x.case_id ?? "")));
        const merged = [...state.casesList];
        for (const row of action.payload.cases) {
          const id = String(row.case_id ?? "");
          if (!id || seen.has(id)) continue;
          seen.add(id);
          merged.push(row);
        }
        state.casesList = merged;
        state.pagination = action.payload.pagination;
        state.isLoadingMore = false;
        state.error = null;
      })
      .addCase(appendNextCasesPage.rejected, (state, action) => {
        state.isLoadingMore = false;
        const msg = (action.payload as string) ?? action.error.message ?? "";
        if (
          msg &&
          msg !== "Stale request" &&
          msg !== "No more pages" &&
          msg !== "Cannot append in this list mode"
        ) {
          state.error = msg;
        }
      });
  },
});

export const {
  setCasesList,
  setCasesPagination,
  clearCasesError,
  clearCases,
  clearCasesClientScope,
  setClientIdFilter,
  clearCasesListScope,
  setCasesSearchArgs,
  setInitialAssignedFilterApplied,
  removeCaseById,
  patchCaseInList,
} = casesSlice.actions;

export const selectCasesList = (state: { cases: CasesState }): CaseItem[] =>
  state.cases?.casesList ?? [];
export const selectCasesPagination = (state: { cases: CasesState }): CasesPagination =>
  state.cases?.pagination ?? { total_cases: 0 };
export const selectCasesLoading = (state: { cases: CasesState }) => state.cases?.isLoading ?? false;
export const selectCasesRefreshing = (state: { cases: CasesState }) =>
  state.cases?.isRefreshing ?? false;
export const selectCasesLoadingMore = (state: { cases: CasesState }) =>
  state.cases?.isLoadingMore ?? false;
export const selectCasesListDataEpoch = (state: { cases: CasesState }) =>
  state.cases?.listDataEpoch ?? 0;
export const selectCasesError = (state: { cases: CasesState }) => state.cases?.error ?? null;
export const selectClientIdFilter = (state: { cases: CasesState }): number | string | null =>
  state.cases?.clientIdFilter ?? null;
export const selectCasesSearchArgs = (state: {
  cases: CasesState;
}): Omit<SearchCasesPayload, "business_id" | "device_token" | "session_id"> | null =>
  state.cases?.searchArgs ?? null;
export const selectCasesInitialAssignedFilterApplied = (state: { cases: CasesState }) =>
  state.cases?.initialAssignedFilterApplied ?? false;

export default casesSlice.reducer;
